import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import type { Submission } from '@/lib/mock-data';
import { usePlayback } from '@/lib/playback';
import { extractYouTubeId, getYouTubeThumbnail } from '@/lib/utils';

interface SongCardProps {
  submission: Submission;
  showSubmitter?: boolean;
  showPoints?: boolean;
  isVoting?: boolean;
  onVote?: (submissionId: string, points: number) => void;
  userPoints?: number;
  leagueId?: string;
}

const SongCard = ({
  submission,
  showSubmitter = false,
  showPoints = false,
  isVoting = false,
  onVote,
  userPoints = 0,
  leagueId,
}: SongCardProps) => {
  const { setPlaylist } = usePlayback();
  const navigate = useNavigate();
  const youtubeId = useMemo(() => {
    if (submission.youtube_video_id) {
      return submission.youtube_video_id;
    }
    if (!submission.video_url) {
      return undefined;
    }
    const extracted = extractYouTubeId(submission.video_url);
    console.log('[SongCard] YouTube ID extraction', { 
      video_url: submission.video_url, 
      youtube_video_id: submission.youtube_video_id,
      extracted 
    });
    return extracted;
  }, [submission.video_url, submission.youtube_video_id]);
  const thumbnail = useMemo(
    () =>
      submission.video_thumbnail ||
      (youtubeId ? getYouTubeThumbnail(youtubeId, 'mqdefault') : submission.spotify_album_image),
    [submission.video_thumbnail, submission.spotify_album_image, youtubeId],
  );

  const handlePlay = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    console.log('[SongCard] handlePlay called', { youtubeId, submissionId: submission.id });
    if (!youtubeId) {
      console.log('[SongCard] No youtubeId, returning');
      return;
    }
    console.log('[SongCard] Setting playlist', { submissionId: submission.id, youtubeId });
    try {
      setPlaylist(
        [
          {
            submissionId: submission.id,
            roundId: submission.round_id,
            leagueId: leagueId,
            title: submission.song_title,
            artist: submission.artist_name,
            youtubeId,
            submitter: submission.username || 'Unknown DJ',
            comment: submission.submitter_comment,
            thumbnail: thumbnail,
          },
        ],
        {
          id: `submission_${submission.id}`,
          label: `${submission.song_title} • ${submission.artist_name}`,
          autoplay: false,
        },
      );
      console.log('[SongCard] Playlist set successfully, navigating to player');
      // Navigate to playlist player page
      navigate('/playlists/player');
    } catch (error) {
      console.error('[SongCard] Error setting playlist:', error);
    }
  }, [setPlaylist, submission, thumbnail, youtubeId, leagueId, navigate]);

  return (
    <Card className="card-hover">
      <CardContent className="p-6">
        {thumbnail && (
          <div className="mb-4">
            <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-lg border border-muted bg-muted/40">
              <button
                type="button"
                onClick={handlePlay}
                disabled={!youtubeId}
                className="relative h-full w-full focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                title={!youtubeId ? 'No YouTube video available' : 'Play song'}
              >
                <img
                  src={thumbnail}
                  alt={`${submission.song_title} thumbnail`}
                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg">
                    <Play className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </button>
            </AspectRatio>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-foreground">
              {submission.song_title}
            </h3>
            <p className="text-muted-foreground">
              by {submission.artist_name}
            </p>
            {showSubmitter && submission.username && (
              <p className="text-sm text-primary mt-1">
                Submitted by {submission.username}
              </p>
            )}
            {submission.providers && (
              <div className="mt-2 flex flex-wrap gap-2">
                {submission.providers.map((provider) => (
                  <Badge key={`${submission.id}-${provider.provider}`} variant="secondary">
                    {provider.provider === 'spotify' ? 'Spotify' : 'YouTube'}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePlay}
              disabled={!youtubeId}
              title={!youtubeId ? 'No YouTube video available' : 'Play song'}
            >
              <Play className="h-4 w-4" />
            </Button>
            {showPoints && (
              <div className="text-right">
                <p className="text-2xl font-bold text-primary">
                  {submission.total_points}
                </p>
                <p className="text-xs text-muted-foreground">points</p>
              </div>
            )}
          </div>
        </div>

        {submission.submitter_comment && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground italic">
              "{submission.submitter_comment}"
            </p>
          </div>
        )}

        {isVoting && onVote && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Your points:</span>
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5].map((points) => (
                  <Button
                    key={points}
                    variant={userPoints === points ? "default" : "outline"}
                    size="sm"
                    onClick={() => onVote(submission.id, points)}
                    className={userPoints === points ? "bg-music-gradient" : ""}
                  >
                    {points}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SongCard;